// Service đăng nhập Google OAuth
// Refactored to strictly use Python Backend via AuthAPI
import { GoogleAuthResponse } from '../../types/auth';
import { API_CONFIG } from '../../config/api.config';
import { authApi } from '../api/authApi';

// ✅ Use centralized API configuration for Google OAuth
const GOOGLE_CLIENT_ID = API_CONFIG.google.clientId;
const GOOGLE_REDIRECT_URI = API_CONFIG.google.redirectUri;

// Debug: Log configuration
if (import.meta.env.DEV) {
  console.log('Google OAuth Configuration (Strict Backend Mode):', {
    clientId: GOOGLE_CLIENT_ID ? GOOGLE_CLIENT_ID.substring(0, 20) + '...' : 'MISSING',
    redirectUri: GOOGLE_REDIRECT_URI,
  });
}

class GoogleAuthService {
  private isGoogleLoaded = false;
  private initializationPromise: Promise<void> | null = null;
  private pendingRole: string = 'student';
  private pendingSourceDomain: string = 'ebook';
  private onLoginSuccess: ((response: GoogleAuthResponse) => void) | null = null;
  private onLoginFailure: ((error: string) => void) | null = null;

  setLoginContext(role: string, domain: string) {
    this.pendingRole = role;
    this.pendingSourceDomain = domain;
  }

  setLoginCallback(
    onSuccess: (response: GoogleAuthResponse) => void,
    onFailure: (error: string) => void
  ) {
    this.onLoginSuccess = onSuccess;
    this.onLoginFailure = onFailure;
  }

  // Load Google OAuth script
  private async loadGoogleScript(): Promise<void> {
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = new Promise((resolve, reject) => {
      if (window.google?.accounts) {
        this.isGoogleLoaded = true;
        resolve();
        return;
      }

      if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
        // Script already exists but window.google not ready? Wait for it.
        const checkInterval = setInterval(() => {
          if (window.google?.accounts) {
            clearInterval(checkInterval);
            this.isGoogleLoaded = true;
            resolve();
          }
        }, 100);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.isGoogleLoaded = true;
        resolve();
      };

      script.onerror = () => {
        this.initializationPromise = null; // Reset on error so we can retry
        reject(new Error('Failed to load Google OAuth script'));
      };

      document.head.appendChild(script);
    });

    return this.initializationPromise;
  }

  // Initialize Google OAuth
  async initialize(): Promise<void> {
    try {
      // Check if current origin matches the configured redirect URI
      const currentOrigin = window.location.origin;
      const configuredOrigin = GOOGLE_REDIRECT_URI.replace('/auth/google/callback', '');

      console.log('GoogleAuthService: Initializing...', { currentOrigin, configuredOrigin });

      // Check if we have a real Google Client ID
      if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'test-client-id') {
        console.warn('Google Client ID not configured. Service disabled.');
        return;
      }

      // Load Google OAuth script
      await this.loadGoogleScript();

      // Initialize Google OAuth
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: this.handleCredentialResponse.bind(this),
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        console.log('✅ Google OAuth initialized successfully');
      } else {
        throw new Error('Google OAuth script not loaded properly');
      }
    } catch (error) {
      console.error('Error initializing Google OAuth:', error);
      this.initializationPromise = null; // Reset on error
    }
  }

  // Handle Google credential response
  async handleCredentialResponse(response: any): Promise<GoogleAuthResponse> {
    console.log('🔐 Google credential response received');

    try {
      if (!response.credential) {
        throw new Error('No credential found in response');
      }

      // Prepare login data for backend
      const loginData = {
        credential: response.credential,
        clientId: response.clientId || GOOGLE_CLIENT_ID,
        role: this.pendingRole,
        source_domain: this.pendingSourceDomain,
      };

      console.log('🚀 Sending credential to Python Backend...', { clientId: loginData.clientId });

      // Call Backend API
      const apiResponse = await authApi.googleLogin(loginData);
      console.log('🐍 Backend Response:', apiResponse);

      if (apiResponse.error) {
        console.error('❌ Backend returned error:', apiResponse.error);
        throw new Error(typeof apiResponse.error === 'string' ? apiResponse.error : 'Login verification failed on server');
      }

      if (!apiResponse.data || !apiResponse.data.access_token) {
        console.error('❌ No access token in response:', apiResponse.data);
        throw new Error('No access token received from backend');
      }

      const { user, access_token } = apiResponse.data;

      // Store authentication tokens
      // Used by both apiClient and potential other consumers
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('jwt_token', access_token);
      localStorage.setItem('user_id', user.id.toString());

      console.log('✅ Login Successful. Tokens stored.');

      const userData = user as any;

      // ✅ FIX: Get permissions from backend or generate from role
      const getRolePermissions = (role: 'student' | 'teacher' | 'admin'): string[] => {
        const map = {
          student: ['lessons:read', 'quizzes:read', 'quizzes:submit', 'profile:read', 'profile:write', 'progress:read'],
          teacher: ['lessons:read', 'lessons:write', 'lessons:delete', 'quizzes:read', 'quizzes:write', 'students:read', 'profile:read', 'profile:write', 'progress:read'],
          admin: [
            'lessons:read', 'lessons:write', 'lessons:delete',
            'quizzes:read', 'quizzes:write', 'quizzes:delete',
            'users:read', 'users:write', 'users:delete', 'users:manage',
            'teachers:approve',
            'students:read', 'students:manage',
            'profile:read', 'profile:write',
            'progress:read', 'progress:manage',
            'reports:read', 'reports:write',
            'system:manage'
          ]
        };
        return map[role] || map.student;
      };

      const userRole = ((userData.role === 'user' ? 'student' : userData.role) || 'student') as 'student' | 'teacher' | 'admin';
      const permissions = userData.permissions || getRolePermissions(userRole);

      // ✅ FIX: Auto-approve teachers on login
      // If role=teacher but teacherInfo.isApproved=false, fix it
      let teacherInfo = userData.teacher_info;
      if (userRole === 'teacher') {
        if (!teacherInfo || !teacherInfo.isApproved) {
          console.log('🔧 Auto-fixing teacher approval status on login');
          teacherInfo = {
            ...(teacherInfo || {}),
            teacherId: teacherInfo?.teacherId || userData.id?.toString(),
            subjects: teacherInfo?.subjects || [],
            experience: teacherInfo?.experience || 0,
            qualifications: teacherInfo?.qualifications || [],
            isApproved: true, // ✅ Auto-approve
            approvedAt: new Date()
          };
        }
      }

      // Return formatted response
      const authResponse: GoogleAuthResponse = {
        user: {
          id: userData.id?.toString() || '',
          email: userData.email || '',
          name: userData.name || userData.full_name || 'User',
          avatar: userData.avatar,
          createdAt: new Date(userData.created_at || Date.now()),
          updatedAt: new Date(userData.updated_at || Date.now()),
          lastLoginAt: new Date(userData.last_login || Date.now()),
          isActive: userData.is_active !== undefined ? userData.is_active : true,
          isVerified: userData.is_verified !== undefined ? userData.is_verified : true,
          provider: 'google',
          providerId: 'google-oauth',
          permissions: permissions,
          role: userRole,
          teacherInfo: teacherInfo
        },
        profile: {
          id: userData.id?.toString() || '',
          userId: userData.id?.toString() || '',
          fullName: userData.name || userData.full_name || 'User',
          createdAt: new Date(),
          updatedAt: new Date(),
          preferences: {
            theme: 'light',
            language: 'vi',
            notifications: true
          }
        },
        token: access_token,
      };

      // Dispatch to AuthContext via registered callback (triggered by renderButton flow)
      if (this.onLoginSuccess) {
        this.onLoginSuccess(authResponse);
      }

      return authResponse;

    } catch (error: any) {
      console.error('💥 Login Error:', error);
      if (this.onLoginFailure) {
        this.onLoginFailure(error instanceof Error ? error.message : 'Login failed');
      }
      throw error;
    }
  }

  // Render Google Sign-In button
  renderSignInButton(elementId: string, options: {
    theme?: 'outline' | 'filled_blue' | 'filled_black';
    size?: 'large' | 'medium' | 'small';
    text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
    shape?: 'rectangular' | 'pill' | 'circle' | 'square';
    logo_alignment?: 'left' | 'center';
  } = {}): void {
    if (!window.google) {
      console.error('Google OAuth not initialized');
      return;
    }

    const element = document.getElementById(elementId);
    if (!element) {
      console.error(`Element with id ${elementId} not found`);
      return;
    }

    window.google.accounts.id.renderButton(element, {
      theme: options.theme || 'outline',
      size: options.size || 'large',
      text: options.text || 'signin_with',
      shape: options.shape || 'rectangular',
      logo_alignment: options.logo_alignment || 'left',
    });
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      if (window.google) {
        window.google.accounts.id.disableAutoSelect();
      }

      // Clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('user_id');

      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  // Get current user ID
  getCurrentUserId(): string | null {
    return localStorage.getItem('user_id');
  }

  // Get current auth token
  getCurrentToken(): string | null {
    return localStorage.getItem('auth_token');
  }
}

// Extend Window interface for Google OAuth
declare global {
  interface Window {
    google: any;
  }
}

export const googleAuthService = new GoogleAuthService();
